import { Chart } from 'chart.js';
import React, { useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

interface HeatPumpTechnologyChartProps {
  location: string;
  geofips: number;
}

interface TechnologyData {
  year: number;
  [key: string]: number; // Dynamic keys for different systems
}

export const HeatPumpTechnologyChart: React.FC<HeatPumpTechnologyChartProps> = ({ location, geofips }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TechnologyData[]>([]);

  useEffect(() => {
    const fetchHeatPumpData = async () => {
      if (!location || !geofips) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch data for the selected location
        const { data: locationData, error: locationError } = await supabaseClient
          .from('heat_pumps')
          .select('year, system, project_cost_per_unit_usd')
          .eq('geo_fips', geofips)
          .order('year', { ascending: true });

        if (locationError) {
          console.error('Error fetching location heat pump data:', locationError);
          throw locationError;
        }

        // Group data by year and system
        const dataByYear = new Map<number, Map<string, number>>();
        locationData?.forEach(record => {
          if (!dataByYear.has(record.year)) {
            dataByYear.set(record.year, new Map());
          }
          dataByYear.get(record.year)!.set(record.system, record.project_cost_per_unit_usd || 0);
        });

        // Get all unique systems
        const allSystems = new Set<string>();
        locationData?.forEach(record => {
          if (record.system) {
            allSystems.add(record.system);
          }
        });

        // Get all years
        const allYears = Array.from(dataByYear.keys()).sort();

        // Process data for chart
        const processedData: TechnologyData[] = allYears.map(year => {
          const yearData: TechnologyData = { year };
          const yearSystems = dataByYear.get(year) || new Map();
          
          allSystems.forEach(system => {
            yearData[system] = yearSystems.get(system) || 0;
          });

          return yearData;
        });

        setData(processedData);

        // Create chart
        if (chartRef.current) {
          const ctx = chartRef.current.getContext('2d');
          if (ctx) {
            if (chartInstance.current) {
              chartInstance.current.destroy();
            }

            const years = processedData.map(d => d.year);
            const systems = Array.from(allSystems);

            // Define colors for different systems
            const systemColors: { [key: string]: string } = {
              'HVAC': '#3b82f6',
              'HPWH': '#ef4444',
              'Heat Pump': '#10b981',
              'Air Source Heat Pump': '#f59e0b',
              'Ground Source Heat Pump': '#8b5cf6',
              'Water Heater': '#06b6d4',
              'Other': '#6b7280'
            };

            const datasets = systems.map(system => ({
              label: system,
              data: processedData.map(d => d[system] || 0),
              borderColor: systemColors[system] || '#6b7280',
              backgroundColor: systemColors[system] ? `${systemColors[system]}20` : '#6b728020',
              borderWidth: 3,
              fill: false,
              pointRadius: 6,
              pointHoverRadius: 8,
              tension: 0.4
            }));

            chartInstance.current = new Chart(ctx, {
              type: 'line',
              data: {
                labels: years,
                datasets
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: standardTitle('Project Cost per Unit by Technology'),
                  subtitle: {
                    display: true,
                    text: `${location} Heat Pump Installations`,
                    font: {
                      size: 14
                    }
                  },
                  legend: standardLegend,
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const value = context.parsed.y;
                        return `${context.dataset.label}: $${(value ?? 0).toLocaleString()}`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Year'
                    }
                  },
                  y: {
                    title: {
                      display: true,
                      text: 'Cost per Unit ($)'
                    },
                    beginAtZero: true,
                    ticks: {
                      callback: (value) => `$${Number(value).toLocaleString()}`
                    }
                  }
                }
              }
            });
          }
        }

      } catch (err) {
        console.error('Error fetching heat pump technology data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load heat pump technology data');
      } finally {
        setLoading(false);
      }
    };

    fetchHeatPumpData();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [location, geofips]);

  if (loading) {
    return <div>Loading heat pump technology data...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div style={{ height: '450px', width: '100%' }}>
      <canvas ref={chartRef} />
    </div>
  );
};
